<template>
  <div class="fts-auth">
    <!-- trigger -->
    <fts-tippy
      :options="{
        interactive: true,
        placement: previewPlacement,
        delay: [500 , 0],
        theme: 'light-border'
      }"
      popper-prevent-overflow
      append-box-class="tippy-box-auth-preview"
      append-content-class="auth-preview"
      @onTrigger="setRights"
    >
      <template #trigger>
        <slot name="trigger" :show="show" :isRight="isRight">
          <font-awesome-icon
            slot="trigger"
            :class="appendToggleClass"
            class="auth-toggle lock fts-text-danger"
            :icon="['fas', 'lock']"
            fixed-width
            @click="show"
            v-if="isRight"
          />
          <font-awesome-icon
            slot="trigger"
            :class="appendToggleClass"
            class="auth-toggle"
            :icon="['fas', 'unlock']"
            fixed-width
            @click="show"
            v-else
          />
        </slot>
      </template>
      <!-- preview -->
      <template #content>
        <div class="auth-preview__header">
          Access <span v-if="title">for {{title}}</span>
        </div>
        <div class="auth-preview__content fts-scrollbar">
          <!-- blank -->
          <div class="auth-preview-blank" v-if="!Boolean(rights && rights.length)">
            <font-awesome-icon
              :icon="['fas', 'check-circle']"
              fixed-width
              size="lg"
              class="fts-text-success"
            />
            Access granted for everyone in the workspace.
          </div>
          <!-- everyone badge -->
          <div class="auth-preview-identity" v-if="isEveryoneAccess">
            <fts-tippy content="Allow">
              <font-awesome-icon
                slot="trigger"
                :icon="['fas', 'check-circle']"
                fixed-width
                class="auth-preview-identity__right fts-text-success"
              />
            </fts-tippy>
            <fts-identity-display
              identity-type="workspace"
              identity-name="Everyone is allow access"
            />
          </div>
          <!-- rights -->
          <div
            v-for="(auth, index) in rights"
            :key="`auth-preview-${auth.entityId}-${index}`"
            class="auth-preview-identity"
          >
            <!-- right -->
            <!-- right:allow -->
            <fts-tippy content="Allow" v-if="auth.r">
              <font-awesome-icon
                slot="trigger"
                :icon="['fas', 'check-circle']"
                fixed-width
                class="auth-preview-identity__right fts-text-success"  
              />
            </fts-tippy>
            <!-- right:deny -->
            <fts-tippy content="Deny" v-else>
              <font-awesome-icon
                slot="trigger"
                :icon="['fas', 'minus-circle']"
                fixed-width
                class="auth-preview-identity__right fts-text-danger"
              />
            </fts-tippy>
            <!-- member -->
            <fts-identity-display
              identity-type="user"
              :identity-name="auth.entity"
              :identity-username="auth.entityName"
              :profile-photo-size="16"
              v-if="auth.entityType === 0"
            />
            <!-- user profile -->
            <fts-identity-display
              :modules="workspaceModules"
              identity-type="profile"
              :identity-id="auth.entityId"
              :identity-name="auth.entity"
              :emoji-size="16"
              v-else-if="auth.entityType === 5"
            />
            <fts-identity-display
              :modules="workspaceModules"
              identity-type="listing"
              :identity-id="auth.entityId"
              :identity-name="auth.entity"
              :emoji-size="16"
              v-else-if="auth.entityType === 6"
            />
            <!-- profile field -->
            <fts-identity-display
              identity-type="profile-field"
              :identity-name="auth.entity"
              v-else-if="auth.entityType === 7"
            />
            <!-- system -->
            <fts-identity-display
              identity-type="workspace"
              :identity-name="auth.entity"
              v-else
            />
          </div>
        </div>
      </template>
    </fts-tippy>
    <!-- modal -->
    <fts-design-modal
      width="1280px"
      :backdrop="false"
      appendContentClass="auth-modal__body"
      class="fts-auth-modal"
      ref="ftsDesignModal"
      @closed="reset"
    >
      <!-- title -->
      <template #title>
        <div class="design-modal-header-std__title">
          <font-awesome-icon
            class="design-modal-header-icon fts-text-danger"
            :icon="['fas', 'lock']"
          />
          <div class="design-modal-header-name">
            Access <span v-if="title">for {{title}}</span>
          </div>
        </div>
      </template>
      <!-- description -->
      <template #description>
        <span class="design-modal-header-tag">Access</span>
        <span class="design-modal-header-description">
          user permission setting
        </span>
      </template>
      <!-- body -->
      <template #body>
        <!-- spinner -->
        <div class="design-modal-spinner" v-if="loading">
          <fts-spinner show />
        </div>
        <template v-else>
          <!-- auth:select -->
          <fts-select
            :form-data="formData"
            :item="item"
            :options="options"
            :reduce="x => x"
            :additional-filter-keys="['entityName']"
            :get-option-key="getOptionKey"
            :clear-search-on-blur="() => true"
            :clear-search-on-select="false"
            :close-on-select="false"
            comfort-design
            infinite-scroll
            class="auth-select"
            @input="selectRole"
            ref="authSelect"
          >
            <template #option="{option}">
              <!-- member -->
              <fts-identity-display
                identity-type="user"
                :identity-name="option.name"
                :identity-username="option.entityName"
                :profile-photo-size="16"
                v-if="option.type === 0"
              />
              <!-- user profile -->
              <fts-identity-display
                :modules="workspaceModules"
                identity-type="profile"
                :identity-id="option.entryId"
                :identity-name="option.name"
                :emoji-size="16"
                v-else-if="option.type === 5"
              />
              <!-- listing -->
              <fts-identity-display
                :modules="workspaceModules"
                identity-type="listing"
                :identity-id="option.entryId"
                :identity-emoji="option.emoji"
                :identity-name="option.name"
                :emoji-size="16"
                class="auth-entity__identity"
                v-else-if="option.type === 6"
              />
              <!-- profile field -->
              <fts-identity-display
                identity-type="profile-field"
                :identity-name="option.name"
                v-else-if="option.type === 7"
              />
              <!-- system -->
              <fts-identity-display
                identity-type="workspace"
                :identity-name="option.name"
                v-else
              />
            </template>
          </fts-select>
          <!-- auth:list -->
          <div class="auth-entity-container">
            <div class="auth-entity-title" v-if="rights.length">
              Only allow access for
            </div>
            <!-- blank -->
            <div class="auth-entity-blank" v-else>
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                fixed-width
                size="lg"
                class="auth-entity-blank__icon fts-text-success"
              />
              Access granted for everyone in the workspace.
            </div>
            <!-- everyone badge -->
            <div class="auth-entity-everyone" v-if="isEveryoneAccess">
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                class="auth-entity-everyone__icon fts-text-success"
                size="lg"
                fixed-width
              />
              <fts-identity-display
                identity-type="workspace"
                identity-name="Everyone is allow access"
              />
            </div>
            <!-- entity -->
            <div class="auth-entity" v-for="auth in rights" :key="auth.key">
              <fts-checkbox :size="16" v-model="auth.r" class="auth-entity__checkbox" />
              <!-- member -->
              <fts-identity-display
                identity-type="user"
                :identity-name="auth.entity"
                :identity-username="auth.entityName"
                :profile-photo-size="20"
                :key="auth.entityId"
                class="auth-entity__identity"
                v-if="auth.entityType === 0"
              />
              <!-- user profile -->
              <fts-identity-display
                :modules="workspaceModules"
                identity-type="profile"
                :identity-id="auth.entityId"
                :identity-name="auth.entity"
                :emoji-size="16"
                class="auth-entity__identity"
                v-else-if="auth.entityType === 5"
              />
              <!-- listing -->
              <fts-identity-display
                :modules="workspaceModules"
                identity-type="listing"
                :identity-id="auth.entityId"
                :identity-name="auth.entity"
                :emoji-size="16"
                class="auth-entity__identity"
                v-else-if="auth.entityType === 6"
              />
              <!-- profile field -->
              <fts-identity-display
                identity-type="profile-field"
                :identity-name="auth.entity"
                class="auth-entity__identity"
                v-else-if="auth.entityType === 7"
              />
              <!-- system -->
              <fts-identity-display
                identity-type="workspace"
                :identity-name="auth.entity"
                class="auth-entity__identity"
                v-else
              />
              <!-- action -->
              <fts-btn
                icon
                text
                class="auth-entity__action"
                @click="removeRole(auth)"
              >
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </fts-btn>
            </div>
          </div>
        </template>
      </template>
      <!-- footer:right -->
      <template #footerRight>
        <fts-btn
          text
          @click="close"
        >
          Cancel
        </fts-btn>

        <fts-btn
          outline
          color="primary"
          :disabled="loading"
          @click="done"
        >
          <font-awesome-icon :icon="['fas', 'check']" /> Ok
        </fts-btn>
      </template>
    </fts-design-modal>
  </div>
</template>

<script>
import { store as canvasDesignStore } from "../store/canvasDesignStore";
import sortAndStringify from "../commons/sortAndStringify";

import FTSDesignModal from "./FTSDesignModal";
import FTSSelect from "../elements/FTSSelect";
import FTSBtn from "../elements/FTSBtn";
import FTSSpinner from "../elements/FTSSpinner";
import FTSTippy from "../elements/FTSTippy";
import FTSCheckbox from "../elements/FTSCheckbox";
import FTSIdentityDisplay from "../elements/FTSIdentityDisplay";

export default {
  props: {
    type: String,
    unitId: String,
    title: String,
    /* tippy */
    previewPlacement: {
      type: String,
      default: 'left-start',
    },
    appendToggleClass: String
  },
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-select": FTSSelect,
    "fts-btn": FTSBtn,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy,
    "fts-checkbox": FTSCheckbox,
    "fts-identity-display": FTSIdentityDisplay
  },
  data: () => ({
    formData: {},
    item: {
      fieldName: "role",
      options: [],
    },
    rights: [],
    loading: false,
  }),
  methods: {
    async show() {
      this.loading = true;
      this.$refs.ftsDesignModal.show();
      this.setRights();

      try {
        const res = await canvasDesignStore.dispatch('retrieveAuthorizationEntities', {
          vectorId: canvasDesignStore.state.vectorId
        });

        this.item.options = res.data.map(x =>
          (x.name === 'Everyone' || x.name === 'Guest') &&
          !x.id ? Object.assign(x, {id: x.name.toLowerCase()}) : x
        );
      } catch(err) {
        console.error(err);
      }

      this.loading = false;
    },
    done() {
      let authorizations = [];
      if (this.type === "vector" || this.type === "import")
        authorizations = [
          ...this.authorizations.filter(x =>
            x.authorizationType != this.type
          )
        ];
      else
        authorizations = [
          ...this.authorizations.filter(x =>
            x.authorizationUnitId != this.unitId
          )
        ];

      authorizations = authorizations.concat(this.rights);
      canvasDesignStore.dispatch('updateAuthorization', authorizations);
      window.eventBus.$emit("builderSave");
      this.close();
    },
    close() {
      this.$refs.ftsDesignModal.close();
    },
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    setRights() {
      this.rights = _.cloneDeep(this.getRights);
      this.rights.filter(x => x.entityType === 5)
        .forEach(x => {
          const res =  [...this.workspaceModules].find(y => y.id == x.entityId);
          if (res && res.name !== x.entity)
            x.entity = res.name;
        });
    },
    getOptionKey(option) {
      try {
        if (typeof option !== 'object') {
          return option;
        }
        if (
          this.item && Array.isArray(this.item.options) &&
          option.hasOwnProperty('id')
        ) {
          const index = this.item.options.indexOf(option);
          if (index > -1) {
            return `${index}-${option.id}`;
          }
        }
        return sortAndStringify(option);
      } catch(err) {
        return console.warn(err);
      }
    },
    selectRole(val) {
      if (val) {
        /* override vSelect behavior */
        try {        
          const dropdownMenu = document.getElementsByClassName('vs__dropdown-menu')[0];
          const selectedEl = dropdownMenu.children[this.$refs.authSelect.$refs.ftsSelect.typeAheadPointer];
          const scrollHeight = dropdownMenu.scrollTop;
          const preElSib = selectedEl && selectedEl.previousElementSibling;
          const nxtElSib = selectedEl && selectedEl.nextElementSibling;

          function setPosition(vm, el) {
            let index = Array.prototype.indexOf.call(document.getElementsByClassName('vs__dropdown-menu')[0].children, el);
            vm.$refs.authSelect.$refs.ftsSelect.typeAheadPointer = index;
            vm.$nextTick(() => {
              document.getElementsByClassName('vs__dropdown-menu')[0].scrollTo(0, scrollHeight);
            });
          } 

          this.$nextTick(() => {
            if (nxtElSib)
              setPosition(this, nxtElSib);
            else if (preElSib) 
              setPosition(this, preElSib);
          });
        } catch(err) {
          console.warn("[DOMException]", err);
        }
        /* set auth */
        const res = {...val};
        this.formData.role = null;

        /* initial set everyone */
        if (val.id !== 'everyone' && !this.rights.length) {
          const everyoneEntity = this.item.options.find(x => x.id === 'everyone');
          if (everyoneEntity) 
            this.rights.push({
              entity: everyoneEntity.name,
              entityName: everyoneEntity.entityName,
              entityId: everyoneEntity.id,
              entityType: everyoneEntity.type,
              authorizationUnitId: this.unitId,
              authorizationType: this.type,
            });
        }

        if (res.type === 7) {
          /* profile field: the row carries the value condition ("Position: Manager") */
          if (
            !this.rights.some(x =>
              x.entityType === 7 &&
              x.entityId == res.entryId &&
              (x.values || []).some(v => v.value === res.value)
            )
          ) {
            this.rights.push({
              entity: res.name,
              entityId: res.entryId,
              entityType: res.type,
              operator: "in",
              values: [{ id: res.valueId, value: res.value }],
              authorizationUnitId: this.unitId,
              authorizationType: this.type,
              r: true,
            });
          }
        } else if (
          !this.rights.some(x =>
            x.entity == res.name &&
            x.entityId == res.id &&
            x.entityType == res.type
          )
        ) {
          this.rights.push({
            entity: res.name,
            entityName: res.entityName,
            entityId: res.entryId,
            entityType: res.type,
            authorizationUnitId: this.unitId,
            authorizationType: this.type,
            r: true,
          });
        }
      }
    },
    removeRole(auth) {
      this.rights.splice(this.rights.indexOf(auth), 1);
    },
  },
  computed: {
    workspaceModules() {
      return canvasDesignStore.getters.workspaceModules;
    },
    options() {
      return this.item.options.filter((x) =>
        !this.rights.some((y) =>
          x.type == 3 || x.type == 4 ?
            x.name === y.entity :
            x.type == 7 ?
              y.entityType == 7 && x.entryId == y.entityId &&
                (y.values || []).some(v => v.value === x.value) :
              x.entryId == y.entityId && x.type == y.entityType
        )
      );
    },
    getRights() {
      try {
        if (this.type === 'vector' || this.type === 'import')
          return this.authorizations.filter(x => x.authorizationType == this.type);
        else
          return this.authorizations.filter(x => x.authorizationUnitId == this.unitId);
      } catch(err) {
        console.error("Auth:GetRight", err);
      }
    },
    isRight() {
      return Boolean(this.getRights && this.getRights.length);
    },
    authorizations() {
      try {
        return canvasDesignStore.state.canvas.authorizations || [];
      } catch(err) {
        console.warn("Auth:Authorizations", err);
        return [];
      }
    },
    isEveryoneAccess() {
      return this.rights && this.rights.length && !this.rights.some(x => x.entityType == 4);
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

/* fts-auth */
.fts-auth {
  text-align: initial;
}
/* auth toggle */
.auth-toggle {
  font-size: .75rem;
  color: var(--fts-default);
  &:hover {
    color: var(--fts-text);
  }
}
/* auth-modal */
.fts-auth-modal {
  .auth-modal__body {
    display: flex;
    align-items: flex-start;
  }
}
/* auth select */
.auth-select {
  @include position($position: sticky, $top: 0);
  $marginRight: 2rem;
  $width: calc(50% - $marginRight);
  flex: 1 1 $width;
  min-width: 400px;
  max-width: $width;
  margin-right: $marginRight;
}
/* auth entity */
.auth-entity-container {
  flex: 1 1 50%;
  min-width: 450px;
  .auth-entity-blank {
    padding: .575rem;
    text-align: center;
    line-height: 1.5;
    .auth-entity-blank__icon {
      margin-right: .375rem;
    }
  }
  .auth-entity-title {
    margin-bottom: .375rem;
    padding: .575rem 0;
    font-size: .875rem;
    color: var(--fts-form-label);
    line-height: 1.5;
  }
  .auth-entity-everyone {
    display: flex;
    align-items: center;
    margin-bottom: .75rem;
    font-size: .875rem;
    .auth-entity-everyone__icon {
      margin-right: 0.75rem;
    }
  }
}
.auth-entity {
  display: flex;
  align-items: center;
  .auth-entity__checkbox {
    margin-right: 1rem;
  }
  .auth-entity__identity {
    flex: 1;
    font-size: .875rem;
    .identity__icon {
      font-size: 1rem;
    }
  }
  .auth-entity__action {
    margin-left: .75rem;
  }
  &:not(:last-child) {
    margin-bottom: 0.25rem;
  }
}
/* auth preview */
.tippy-box-auth-preview {
  padding: 0;
}
.auth-preview {
  max-height: 300px;
  .auth-preview__header {
    @include text-overflow-ellipsis;
    flex-shrink: 0;
    padding: .75rem;
    border-bottom: 1px solid var(--fts-border);
    font-size: 0.875rem;
    font-weight: 500;
    z-index: 1;
  }
  .auth-preview__content {
    padding-top: .375rem;
    padding-bottom: .375rem;
    .auth-preview-blank {
      padding: .75rem;
      text-align: center;
      color: var(--fts-default);
      line-height: 1.5;
    }
    .auth-everyone-badge {
      margin-left: .375rem;
      margin-right: .375rem;
      font-size: .75rem;
    }
    .auth-preview-identity {
      display: flex;
      align-items: center;
      padding: .375rem .75rem;
      .identity__icon {
        font-size: .875rem;;
      }
      .identity__badge {
        font-size: 0.625rem;
      }
      .auth-preview-identity__right {
        margin-right: .75rem;
        font-size: .875rem;
      }
    }
  }
}
</style>